import { UserOutlined } from '@ant-design/icons';
import { type IMessage } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Avatar,
  Button,
  Col,
  Form,
  Input,
  Row,
  Skeleton,
  Space,
  theme,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { sendMessage } from '../../reducers/messenger/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';

interface IForm {
  message: string;
}

function MessengerMessages(): React.ReactElement {
  const dispatch = useAppDispatch();
  const messengerInProcess = useSelector(
    (s: RootState) => s.Messenger.inProcess,
  );
  const messengerLeadUUID = useSelector(
    (s: RootState) => s.Messenger.lead.uuid,
  );
  const messengerMessages = useSelector((s: RootState) => s.Messenger.messages);

  const { token } = theme.useToken();
  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ message }: IForm, leadUUID: string | undefined): void => {
      if (!leadUUID || !message) {
        return;
      }

      dispatch(sendMessage(leadUUID, message));

      form.setFieldsValue({
        message: '',
      });
    },
    [dispatch, form],
  );

  if (messengerInProcess) {
    return <Skeleton active />;
  }

  if (!messengerLeadUUID) {
    return <Typography.Text>Select lead from list!</Typography.Text>;
  }

  return (
    <Row>
      <Col span={24}>
        <Form
          form={form}
          onFinish={(values: IForm): void =>
            onFinish(values, messengerLeadUUID)
          }
        >
          <Row gutter={SPACING.none}>
            <Col span={19}>
              <Form.Item
                name="message"
                rules={[
                  {
                    required: true,
                  },
                  {
                    type: 'string',
                  },
                ]}
              >
                <Input placeholder="Message" disabled={messengerInProcess} />
              </Form.Item>
            </Col>
            <Col span={4} offset={1}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                disabled={messengerInProcess}
              >
                Send
              </Button>
            </Col>
          </Row>
        </Form>
      </Col>

      <Col span={24}>
        <div className="comment-list">
          <Typography.Text type="secondary">
            {messengerMessages.length} messages
          </Typography.Text>
          {messengerMessages.map((item: IMessage) => (
            <div key={item.timestamp}>
              <Space align="start" size={SPACING.sm}>
                <Avatar
                  size={40}
                  icon={<UserOutlined />}
                  style={{
                    backgroundColor:
                      item.direction === 'in'
                        ? token.colorSuccess
                        : token.colorPrimary,
                  }}
                />
                <div>
                  <Space align="center" size={SPACING.xs}>
                    <Typography.Text strong>
                      {dayjs(item.timestamp * 1000).format(
                        EDateFormat.DATE_TIME,
                      )}
                    </Typography.Text>
                    <CopyToClipboard
                      text={item.message}
                      size="small"
                      type="text"
                    />
                  </Space>
                  <br />
                  <Typography.Text>{item.message}</Typography.Text>
                </div>
              </Space>
            </div>
          ))}
        </div>
      </Col>
    </Row>
  );
}

export default MessengerMessages;
